import { handleError, hasError, tryCatch } from '@repo/shared/api/helpers'
import { AdminBeneficiariesRoutes } from '@repo/shared/api-routes/admin/beneficiaries'
import {
  AdminBeneficiariesFiltersRoutes,
  BeneficiariesFiltersRoutes,
} from '@repo/shared/routes-beneficiary-filters'
import { AxiosInstance } from 'axios'

import { resetDateToUTCStartOfDayEpoch } from '@/components/beneficiary/list/filters/helpers'
import { IBeneficiariesApi } from '@/spi/api/beneficiaries'

export const buildBeneficiariesApi = (axios: AxiosInstance): IBeneficiariesApi => ({
  search: async ({ organizationId, filters, sortOption, page, perPage, scope }) => {
    const route =
      scope === 'admin' ? AdminBeneficiariesFiltersRoutes.search : BeneficiariesFiltersRoutes.search

    // Reset date to UTC start of day before sending to backend
    const cleanedFilters = filters.map((filter) => {
      if (filter.type === 'date') {
        // FIXME: startOfDay is sunday for US locale
        const newFilter = { key: filter.key, type: filter.type }
        switch (filter.operator) {
          case 'between':
            return {
              ...newFilter,
              operator: filter.operator,
              value: resetDateToUTCStartOfDayEpoch(new Date(filter.value)),
              otherValue: resetDateToUTCStartOfDayEpoch(new Date(filter.otherValue)),
            }

          case 'eq':
          case 'gt':
          case 'lt':
          case 'neq':
            return {
              ...newFilter,
              operator: filter.operator,
              value: resetDateToUTCStartOfDayEpoch(new Date(filter.value)),
            }

          case 'in_the_last':
          case 'in_the_next':
            return {
              ...newFilter,
              operator: filter.operator,
              days: filter.days,
            }

          case 'current_week':
          case 'current_month':
          case 'current_year':
            return {
              ...newFilter,
              operator: filter.operator,
            }

          case 'is_null':
          case 'is_not_null':
            return {
              ...newFilter,
              operator: filter.operator,
            }

          default:
            throw new Error(
              // @ts-expect-error TS thinks filter.operator is never here
              `Unsupported operator for date filter in beneficiaries search: ${filter.operator}`,
            )
        }
      }
      return filter
    })
    const response = await tryCatch(async () =>
      axios.post<typeof BeneficiariesFiltersRoutes.search.response>(
        route.getPath({ organizationId }),
        {
          payload: { filters: cleanedFilters, sortOption, page, perPage },
        } satisfies typeof BeneficiariesFiltersRoutes.search.request,
      ),
    )

    if (hasError(response)) return handleError(response)

    return {
      beneficiaries: response.data,
      meta: { maxPage: response.pagination.lastPage, totalItems: response.pagination.total },
    }
  },

  deleteOne: async ({ organizationId, beneficiaryId }) => {
    const response = await tryCatch(async () =>
      axios.delete<typeof AdminBeneficiariesRoutes.deleteOne.response>(
        AdminBeneficiariesRoutes.deleteOne.getPath({ organizationId, beneficiaryId }),
      ),
    )
    if (hasError(response)) return handleError(response)
    return response.data
  },

  refreshAllStructuredData: async ({ organizationId }) => {
    const response = await tryCatch(async () =>
      axios.patch<typeof AdminBeneficiariesRoutes.outdateStructuredDataForAll.response>(
        AdminBeneficiariesRoutes.outdateStructuredDataForAll.getPath({
          organizationId,
        }),
        {
          payload: { origin: 'admin' },
        } satisfies typeof AdminBeneficiariesRoutes.outdateStructuredDataForAll.request,
      ),
    )
    if (hasError(response)) return handleError(response)
    return response.data
  },

  getAllStructuredDataCurrentState: async ({ organizationId }) => {
    const response = await tryCatch(async () =>
      axios.get<typeof AdminBeneficiariesRoutes.getAllStructuredDataCurrentState.response>(
        AdminBeneficiariesRoutes.getAllStructuredDataCurrentState.getPath({
          organizationId,
        }),
      ),
    )
    if (hasError(response)) return handleError(response)
    return response.data
  },

  exportToCsv: async ({ organizationId, filters, sortOption, columnIds, scope }) => {
    const route =
      scope === 'admin'
        ? AdminBeneficiariesFiltersRoutes.exportToCsv
        : BeneficiariesFiltersRoutes.exportToCsv

    const response = await tryCatch(async () =>
      axios.post<typeof BeneficiariesFiltersRoutes.exportToCsv.response>(
        route.getPath({ organizationId }),
        {
          payload: { filters, sortOption, columnIds },
        } satisfies typeof BeneficiariesFiltersRoutes.exportToCsv.request,
      ),
    )
    if (hasError(response)) return handleError(response)
    return response.data
  },
})
